import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { formatLocalizedDate } from 'i18n/formatDate'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'

interface IUseSuggestedTimesProps {
  selectedGuests: IAssigneeOptions[]
  internalAttendees: IAssigneeOptions[]
  userAvailabilityMap: Record<string, any>
  getAttendeeEvents: {
    [key: string]: Array<{
      date: string
      start: number
      end: number
      title?: string
      startMinute: number
      endMinute: number
    }>
  }
}

export const useSuggestedTimes = ({
  selectedGuests,
  internalAttendees,
  userAvailabilityMap,
  getAttendeeEvents,
}: IUseSuggestedTimesProps) => {
  const { t, i18n } = useTranslation()
  const [suggestedTimes, setSuggestedTimes] = useState<
    Array<{ date: string; time: string; dateTime: Date }>
  >([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)

  useEffect(() => {
    if (selectedGuests.length === 0) {
      setSuggestedTimes([])
      setLoadingSuggestions(false)
      return
    }

    const calculateTimes = () => {
      const allDataLoaded = internalAttendees.every(
        (attendee) => userAvailabilityMap[attendee.value]?.type === 'events'
      )

      if (!allDataLoaded) {
        setSuggestedTimes([])
        setLoadingSuggestions(true)
        return
      }

      const suggestions: Array<{ date: string; time: string; dateTime: Date }> = []
      const now = new Date()
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const getDateLabel = (date: Date) => {
        const targetDate = new Date(date)
        targetDate.setHours(0, 0, 0, 0)
        const diffDays = Math.floor(
          (targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        )

        if (diffDays === 0) return t('calendar.today')
        if (diffDays === 1) return t('calendar.suggestedTimes.tomorrow')
        return formatLocalizedDate(date, i18n.language, 'ddd ll')
      }

      const hasConflict = (slotStart: Date, slotEnd: Date): boolean => {
        const slotStartTime = slotStart.getTime()
        const slotEndTime = slotEnd.getTime()

        for (const attendee of internalAttendees) {
          const events = getAttendeeEvents[attendee.value] || []

          for (const event of events) {
            if (event.date !== slotStart.toDateString()) continue

            const eventDate = new Date(slotStart)
            const eventStart = new Date(eventDate.setHours(event.start, event.startMinute, 0, 0))
            const eventEnd = new Date(eventDate.setHours(event.end, event.endMinute, 0, 0))

            const eventStartTime = eventStart.getTime()
            const eventEndTime = eventEnd.getTime()

            if (slotStartTime < eventEndTime && slotEndTime > eventStartTime) {
              return true
            }
          }
        }

        return false
      }

      let searchDate = new Date(now)
      const currentMinutes = searchDate.getMinutes()
      const roundedMinutes = Math.ceil(currentMinutes / 30) * 30
      searchDate.setMinutes(roundedMinutes, 0, 0)

      if (roundedMinutes >= 60) {
        searchDate.setHours(searchDate.getHours() + 1, 0, 0, 0)
      }

      const businessStart = 9
      const businessEnd = 17
      let slotsFound = 0
      let daysSearched = 0
      const maxDaysToSearch = 14
      const meetingDuration = 30

      while (slotsFound < 5 && daysSearched < maxDaysToSearch) {
        const hour = searchDate.getHours()

        if (hour >= businessStart && hour < businessEnd) {
          const slotEnd = new Date(searchDate.getTime() + meetingDuration * 60 * 1000)

          if (
            slotEnd.getHours() < businessEnd ||
            (slotEnd.getHours() === businessEnd && slotEnd.getMinutes() === 0)
          ) {
            if (!hasConflict(searchDate, slotEnd)) {
              const timeString = formatLocalizedDate(searchDate, i18n.language, 'LT')

              suggestions.push({
                date: getDateLabel(searchDate),
                time: timeString,
                dateTime: new Date(searchDate),
              })

              slotsFound++
            }
          }
        }

        searchDate.setMinutes(searchDate.getMinutes() + 30)

        const nextHour = searchDate.getHours()
        if (nextHour >= businessEnd || nextHour < businessStart) {
          searchDate.setDate(searchDate.getDate() + 1)
          searchDate.setHours(businessStart, 0, 0, 0)
          daysSearched++
        }
      }

      setSuggestedTimes(suggestions)
      setLoadingSuggestions(false)
    }

    setLoadingSuggestions(true)
    calculateTimes()
  }, [selectedGuests, userAvailabilityMap, internalAttendees, getAttendeeEvents, t, i18n.language])

  return { suggestedTimes, loadingSuggestions }
}
